import { useCallback, useEffect, useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { AlertCircle, Clock, Eye, Pencil, Plus, PlusCircle, Trash2, X } from "lucide-react";
import {
    addBox,
    addSlots,
    deleteBoxes,
    deleteSlots,
    getBoxbyId,
    getSlotByDate,
    postTurfSlotsWeeklySchedule,
    updateBox,
    updateSlot,
} from "../service/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";

type PartnerTurfSlotsModalProps = {
    open: boolean;
    onClose: () => void;
    partnerId: number | null;
    partnerName: string;
};

const emptyBoxForm = { name: "", type: "", is_active: false };
const emptySlotForm = {
    date: "",
    from_time: "",
    to_time: "",
    price: "",
    is_available: false,
    reason: "" as string | number,
};

function toDateInputValue(raw: string | undefined): string {
    if (!raw) return "";
    try {
        const d = parseISO(String(raw).slice(0, 10));
        return isValid(d) ? format(d, "yyyy-MM-dd") : "";
    } catch {
        return "";
    }
}

/** Normalizes `get_slot_by_date` response to a slot row array. */
function extractGetSlotByDateList(res: { data?: unknown }): any[] {
    const d = res?.data as any;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d)) return d;
    return [];
}

type WeeklyBulkRow = {
    key: string;
    start_time: string;
    end_time: string;
    price: string;
    is_active: boolean;
};

function newWeeklyBulkRow(): WeeklyBulkRow {
    return {
        key: `wb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        start_time: "",
        end_time: "",
        price: "",
        is_active: true,
    };
}

function toApiWeeklyTime(t: string): string {
    const s = (t || "").trim();
    if (!s) return "00:00:00";
    if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
    if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
    return `${s.slice(0, 5)}:00`;
}

function rowsToWeeklyApiSlots(rows: WeeklyBulkRow[]) {
    return rows.map((r) => ({
        start_time: toApiWeeklyTime(r.start_time),
        end_time: toApiWeeklyTime(r.end_time),
        price: Number(r.price) || 0,
        is_active: r.is_active ? 1 : 0,
    }));
}

const WEEKDAYS_FOR_BULK = [
    { v: 0, label: "Sunday" },
    { v: 1, label: "Monday" },
    { v: 2, label: "Tuesday" },
    { v: 3, label: "Wednesday" },
    { v: 4, label: "Thursday" },
    { v: 5, label: "Friday" },
    { v: 6, label: "Saturday" },
] as const;

type PerDayBulkState = { include: boolean; rows: WeeklyBulkRow[] };

function initialPerDayBulkState(): PerDayBulkState[] {
    return WEEKDAYS_FOR_BULK.map(() => ({ include: false, rows: [] }));
}

export default function PartnerTurfSlotsModal({
    open,
    onClose,
    partnerId,
    partnerName,
}: PartnerTurfSlotsModalProps) {
    const [boxes, setBoxes] = useState<any[]>([]);
    const [loadingBoxes, setLoadingBoxes] = useState(false);
    const [boxModalOpen, setBoxModalOpen] = useState(false);
    const [boxMode, setBoxMode] = useState<"add" | "update">("add");
    const [editBoxId, setEditBoxId] = useState<number | null>(null);
    const [boxForm, setBoxForm] = useState(emptyBoxForm);
    const [boxErrors, setBoxErrors] = useState<Record<string, string>>({});

    const [deleteBoxModal, setDeleteBoxModal] = useState(false);
    const [boxToDeleteId, setBoxToDeleteId] = useState<number | null>(null);

    const [viewSlotsOpen, setViewSlotsOpen] = useState(false);
    const [slotBoxId, setSlotBoxId] = useState<number | null>(null);
    const [slotBoxName, setSlotBoxName] = useState("");
    const [slotsForSelectedDate, setSlotsForSelectedDate] = useState<any[]>([]);
    const [loadingSlotsForDate, setLoadingSlotsForDate] = useState(false);
    const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));

    const [slotModalOpen, setSlotModalOpen] = useState(false);
    const [slotMode, setSlotMode] = useState<"add" | "update">("add");
    const [slotId, setSlotId] = useState<number | null>(null);
    const [slotForm, setSlotForm] = useState(emptySlotForm);
    const [slotErrors, setSlotErrors] = useState<Record<string, string>>({});

    const [deleteSlotModal, setDeleteSlotModal] = useState(false);
    const [slotToDeleteId, setSlotToDeleteId] = useState<number | null>(null);

    /** True when adding a dated row prefilled from a schedule-only (resolved) slot. */
    const [slotOverrideFromSchedule, setSlotOverrideFromSchedule] = useState(false);
    const [scheduleRowExplainOpen, setScheduleRowExplainOpen] = useState(false);
    const [scheduleRowForExplain, setScheduleRowForExplain] = useState<any>(null);

    const [saving, setSaving] = useState(false);

    const [weeklyBulkOpen, setWeeklyBulkOpen] = useState(false);
    const [weeklyBulkMode, setWeeklyBulkMode] = useState<"same" | "per_day">("same");
    const [weeklyBulkSameRows, setWeeklyBulkSameRows] = useState<WeeklyBulkRow[]>(() => [newWeeklyBulkRow()]);
    const [weeklyBulkPerDay, setWeeklyBulkPerDay] = useState<PerDayBulkState[]>(() => initialPerDayBulkState());
    const [weeklyBulkMsg, setWeeklyBulkMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

    const loadBoxes = useCallback(async () => {
        if (partnerId == null) return;
        setLoadingBoxes(true);
        try {
            const res = await getBoxbyId(partnerId);
            const list = res?.data?.data;
            setBoxes(Array.isArray(list) ? list : []);
        } catch {
            setBoxes([]);
        } finally {
            setLoadingBoxes(false);
        }
    }, [partnerId]);

    useEffect(() => {
        if (open && partnerId != null) {
            loadBoxes();
        }
    }, [open, partnerId, loadBoxes]);

    const resetAndClose = () => {
        setBoxes([]);
        setBoxModalOpen(false);
        setViewSlotsOpen(false);
        setSlotModalOpen(false);
        setDeleteBoxModal(false);
        setDeleteSlotModal(false);
        setSlotBoxId(null);
        setSlotOverrideFromSchedule(false);
        setScheduleRowExplainOpen(false);
        setScheduleRowForExplain(null);
        setWeeklyBulkOpen(false);
        setWeeklyBulkMsg(null);
        onClose();
    };

    const openWeeklyBulk = () => {
        setWeeklyBulkMode("same");
        setWeeklyBulkSameRows([newWeeklyBulkRow()]);
        setWeeklyBulkPerDay(initialPerDayBulkState());
        setWeeklyBulkMsg(null);
        setWeeklyBulkOpen(true);
    };

    const submitWeeklyBulk = async () => {
        if (partnerId == null) return;
        if (weeklyBulkMode === "same") {
            if (weeklyBulkSameRows.length === 0) {
                setWeeklyBulkMsg({ type: "err", text: "Add at least one time window, or use Selected days only to clear weekdays." });
                return;
            }
            const invalid = weeklyBulkSameRows.some(
                (r) => !r.start_time || !r.end_time || !String(r.price).trim(),
            );
            if (invalid) {
                setWeeklyBulkMsg({ type: "err", text: "Each row needs start, end, and price." });
                return;
            }
            setSaving(true);
            setWeeklyBulkMsg(null);
            try {
                await postTurfSlotsWeeklySchedule({
                    turf_id: partnerId,
                    same_for_all_days: true,
                    slots: rowsToWeeklyApiSlots(weeklyBulkSameRows),
                });
                setWeeklyBulkMsg({ type: "ok", text: "Weekly schedule saved for all seven days." });
            } catch {
                setWeeklyBulkMsg({ type: "err", text: "Save failed. Check values and try again." });
            } finally {
                setSaving(false);
            }
        } else {
            const touched = weeklyBulkPerDay
                .map((d, i) => ({
                    day_of_week: WEEKDAYS_FOR_BULK[i].v,
                    include: d.include,
                    rows: d.rows,
                }))
                .filter((d) => d.include);
            if (touched.length === 0) {
                setWeeklyBulkMsg({
                    type: "err",
                    text: "Select at least one day to update, or switch to Same every day.",
                });
                return;
            }
            for (const d of touched) {
                if (d.rows.length === 0) continue;
                const invalid = d.rows.some((r) => !r.start_time || !r.end_time || !String(r.price).trim());
                if (invalid) {
                    setWeeklyBulkMsg({
                        type: "err",
                        text: "For each day with slots, every row needs start, end, and price (or clear all rows to close that day).",
                    });
                    return;
                }
            }
            setSaving(true);
            setWeeklyBulkMsg(null);
            try {
                await postTurfSlotsWeeklySchedule({
                    turf_id: partnerId,
                    days: touched.map((d) => ({
                        day_of_week: d.day_of_week,
                        slots: d.rows.length === 0 ? [] : rowsToWeeklyApiSlots(d.rows),
                    })),
                });
                setWeeklyBulkMsg({ type: "ok", text: "Weekly schedule updated for the selected days." });
            } catch {
                setWeeklyBulkMsg({ type: "err", text: "Save failed." });
            } finally {
                setSaving(false);
            }
        }
    };

    const openBoxModal = (item?: any) => {
        setBoxErrors({});
        if (item) {
            setBoxMode("update");
            setEditBoxId(item.id);
            setBoxForm({
                name: item.name ?? "",
                type: item.type ?? "",
                is_active: item.is_active === 1,
            });
        } else {
            setBoxMode("add");
            setEditBoxId(null);
            setBoxForm(emptyBoxForm);
        }
        setBoxModalOpen(true);
    };

    const submitBox = async (e: React.FormEvent) => {
        e.preventDefault();
        const err: Record<string, string> = {};
        if (!boxForm.name.trim()) err.name = "Name is required";
        if (!boxForm.type.trim()) err.type = "Type is required";
        setBoxErrors(err);
        if (Object.keys(err).length || partnerId == null) return;

        setSaving(true);
        try {
            if (boxMode === "add") {
                await addBox({
                    name: boxForm.name,
                    type: boxForm.type,
                    is_active: boxForm.is_active,
                    partnerId,
                });
            } else if (editBoxId != null) {
                await updateBox({
                    id: editBoxId,
                    name: boxForm.name,
                    type: boxForm.type,
                    is_active: boxForm.is_active,
                    partnerId,
                });
            }
            setBoxModalOpen(false);
            await loadBoxes();
        } catch {
            /* keep modal open */
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteBox = (id: number) => {
        setBoxToDeleteId(id);
        setDeleteBoxModal(true);
    };

    const runDeleteBox = async () => {
        if (boxToDeleteId == null) return;
        setSaving(true);
        try {
            await deleteBoxes({ id: boxToDeleteId });
            setDeleteBoxModal(false);
            setBoxToDeleteId(null);
            if (slotBoxId === boxToDeleteId) {
                setViewSlotsOpen(false);
                setSlotBoxId(null);
            }
            await loadBoxes();
        } finally {
            setSaving(false);
        }
    };

    const loadSlotsForDate = useCallback(async (boxId: number, dateIso: string) => {
        if (!dateIso || !isValid(parseISO(dateIso))) {
            setSlotsForSelectedDate([]);
            return;
        }
        setLoadingSlotsForDate(true);
        try {
            const res = await getSlotByDate({ box_id: boxId, date: dateIso });
            setSlotsForSelectedDate(extractGetSlotByDateList(res));
        } catch {
            setSlotsForSelectedDate([]);
        } finally {
            setLoadingSlotsForDate(false);
        }
    }, []);

    useEffect(() => {
        if (!viewSlotsOpen || slotBoxId == null) return;
        const d = parseISO(selectedDate);
        if (!isValid(d)) return;
        loadSlotsForDate(slotBoxId, selectedDate);
    }, [viewSlotsOpen, slotBoxId, selectedDate, loadSlotsForDate]);

    const openViewSlots = (box: any) => {
        setSlotBoxId(box.id);
        setSlotBoxName(box.name ?? `Box #${box.id}`);
        setSelectedDate(format(new Date(), "yyyy-MM-dd"));
        setViewSlotsOpen(true);
    };

    const openSlotModal = (box: any, mode: "add" | "update", slot?: any) => {
        setSlotErrors({});
        setSlotBoxId(box.id);
        if (mode === "add") {
            setSlotMode("add");
            setSlotId(null);
            if (slot) {
                setSlotOverrideFromSchedule(
                    slot.id == null || slot.resolved_from === "turf_availability",
                );
                setSlotForm({
                    date: toDateInputValue(slot.date) || selectedDate,
                    from_time: String(slot.from_time ?? "").slice(0, 5),
                    to_time: String(slot.to_time ?? "").slice(0, 5),
                    price: String(slot.price ?? ""),
                    is_available: slot.is_available === 1 || slot.is_available === true,
                    reason:
                        slot.not_available_reason === 0 || slot.not_available_reason === 1
                            ? slot.not_available_reason
                            : "",
                });
            } else {
                setSlotOverrideFromSchedule(false);
                setSlotForm({
                    date: selectedDate,
                    from_time: "",
                    to_time: "",
                    price: "",
                    is_available: false,
                    reason: "",
                });
            }
        } else if (slot && slot.id != null) {
            setSlotOverrideFromSchedule(false);
            setSlotMode("update");
            setSlotId(slot.id);
            setSlotForm({
                date: toDateInputValue(slot.date) || selectedDate,
                from_time: String(slot.from_time ?? "").slice(0, 5),
                to_time: String(slot.to_time ?? "").slice(0, 5),
                price: String(slot.price ?? ""),
                is_available: slot.is_available === 1 || slot.is_available === true,
                reason:
                    slot.not_available_reason === 0 || slot.not_available_reason === 1
                        ? slot.not_available_reason
                        : "",
            });
        }
        setSlotModalOpen(true);
    };

    const openScheduleRowDeleteExplain = (slot: any) => {
        setScheduleRowForExplain(slot);
        setScheduleRowExplainOpen(true);
    };

    const startOverrideFromScheduleRow = () => {
        const slot = scheduleRowForExplain;
        const box = boxes.find((b: any) => b.id === slotBoxId);
        setScheduleRowExplainOpen(false);
        setScheduleRowForExplain(null);
        if (box && slot) openSlotModal(box, "add", slot);
    };

    const closeSlotModal = () => {
        setSlotModalOpen(false);
        setSlotOverrideFromSchedule(false);
    };

    const submitSlot = async (e: React.FormEvent) => {
        e.preventDefault();
        const err: Record<string, string> = {};
        if (!slotForm.date) err.date = "Date is required";
        if (!slotForm.from_time) err.from_time = "From time is required";
        if (!slotForm.to_time) err.to_time = "To time is required";
        if (!slotForm.price.trim()) err.price = "Price is required";
        if (!slotForm.is_available && (slotForm.reason === "" || slotForm.reason === undefined)) {
            err.reason = "Select a reason when slot is unavailable";
        }
        setSlotErrors(err);
        if (Object.keys(err).length || slotBoxId == null) return;

        const not_available_reason = slotForm.is_available
            ? null
            : Number(slotForm.reason);

        setSaving(true);
        try {
            if (slotMode === "add") {
                await addSlots({
                    date: slotForm.date,
                    from_time: slotForm.from_time,
                    to_time: slotForm.to_time,
                    price: slotForm.price,
                    is_available: slotForm.is_available,
                    not_available_reason,
                    box_id: slotBoxId,
                });
            } else if (slotId != null) {
                await updateSlot({
                    id: slotId,
                    date: slotForm.date,
                    from_time: slotForm.from_time,
                    to_time: slotForm.to_time,
                    price: slotForm.price,
                    is_available: slotForm.is_available,
                    not_available_reason,
                    box_id: slotBoxId,
                });
            }
            closeSlotModal();
            if (viewSlotsOpen && slotBoxId != null) {
                const refreshDate = slotForm.date || selectedDate;
                await loadSlotsForDate(slotBoxId, refreshDate);
                if (refreshDate !== selectedDate) setSelectedDate(refreshDate);
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteSlot = (slot: any) => {
        if (slot?.id == null) return;
        setSlotToDeleteId(slot.id);
        setDeleteSlotModal(true);
    };

    const runDeleteSlot = async () => {
        if (slotToDeleteId == null) return;
        setSaving(true);
        try {
            await deleteSlots({ id: slotToDeleteId });
            setDeleteSlotModal(false);
            setSlotToDeleteId(null);
            if (slotBoxId != null) await loadSlotsForDate(slotBoxId, selectedDate);
        } finally {
            setSaving(false);
        }
    };

    const sortedDaySlots = useMemo(
        () =>
            [...slotsForSelectedDate].sort((a, b) =>
                String(a.from_time ?? "").localeCompare(String(b.from_time ?? "")),
            ),
        [slotsForSelectedDate],
    );
    const selectedDateLabel =
        selectedDate && isValid(parseISO(selectedDate))
            ? format(parseISO(selectedDate), "EEEE, MMM d, yyyy")
            : selectedDate;

    if (!open || partnerId == null) return null;

    return (
        <>
            {/* ── Main turf list panel (full-screen overlay, not Dialog) ── */}
            <div className="fixed inset-0 bg-black/50 z-[100]" aria-hidden onClick={() => !saving && resetAndClose()} />
            <div
                className="fixed inset-0 z-[110] flex items-center justify-center p-2 sm:p-4 pointer-events-none"
            >
                <div
                    className="pointer-events-auto bg-background rounded-xl shadow-2xl border w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="shrink-0 px-5 sm:px-8 pt-6 pb-5 border-b flex items-start justify-between gap-4">
                        <div className="min-w-0 space-y-1">
                            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                Partner scheduling
                            </p>
                            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                                Turfs &amp; availability
                            </h2>
                            <p className="text-sm text-muted-foreground truncate max-w-[min(100%,42rem)]" title={partnerName}>
                                {partnerName}
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-muted-foreground"
                            aria-label="Close"
                            disabled={saving}
                            onClick={resetAndClose}
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 sm:py-6 space-y-5 bg-muted/10">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                            <div className="space-y-1 max-w-xl">
                                <h3 className="text-sm font-semibold">Courts &amp; turfs</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Each turf is a bookable unit. Use{" "}
                                    <strong className="font-medium">View schedule</strong> for dated
                                    slots. <strong className="font-medium">Partner weekly hours</strong>{" "}
                                    sets the recurring template for this partner (<span className="font-mono text-xs">turf_id</span>).
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button variant="outline" size="sm" onClick={openWeeklyBulk} className="gap-2">
                                    <Clock className="h-4 w-4" />
                                    Partner weekly hours
                                </Button>
                                <Button size="sm" onClick={() => openBoxModal()} className="gap-2">
                                    <Plus className="h-4 w-4" />
                                    Add turf
                                </Button>
                            </div>
                        </div>

                        {loadingBoxes ? (
                            <div className="flex flex-col items-center justify-center gap-3 py-20 rounded-2xl border border-dashed">
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                <p className="text-sm text-muted-foreground">Loading turfs…</p>
                            </div>
                        ) : boxes.length === 0 ? (
                            <div className="rounded-2xl border border-dashed px-6 py-14 text-center">
                                <p className="text-base font-medium">No turfs yet</p>
                                <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
                                    Add a turf to represent a court or pitch. You can then set its daily schedule and dated slots.
                                </p>
                                <Button size="sm" className="mt-6 gap-2" onClick={() => openBoxModal()}>
                                    <Plus className="h-4 w-4" />
                                    Add your first turf
                                </Button>
                            </div>
                        ) : (
                            <ul className="grid gap-4 sm:grid-cols-2">
                                {boxes.map((item: any) => {
                                    const active = item.is_active === 1;
                                    return (
                                        <li
                                            key={item.id}
                                            className="rounded-2xl border bg-card p-4 sm:p-5 shadow-sm flex flex-col gap-4"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 space-y-1">
                                                    <h4 className="font-semibold text-base leading-snug truncate pr-2">
                                                        {item.name}
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        {item.type ? (
                                                            <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium">
                                                                {item.type}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted-foreground/60">No type set</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <Badge variant={active ? "default" : "secondary"} className="shrink-0">
                                                    {active ? "Active" : "Inactive"}
                                                </Badge>
                                            </div>
                                            <div className="flex flex-col gap-2 pt-1 border-t">
                                                <Button
                                                    size="sm"
                                                    className="w-full gap-2"
                                                    onClick={() => openViewSlots(item)}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                    View schedule
                                                </Button>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-1.5"
                                                        onClick={() => {
                                                            setSlotBoxId(item.id);
                                                            setSelectedDate(format(new Date(), "yyyy-MM-dd"));
                                                            openSlotModal(item, "add");
                                                        }}
                                                    >
                                                        <PlusCircle className="h-4 w-4" />
                                                        Add slot
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-1.5"
                                                        onClick={() => openBoxModal(item)}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                        Edit turf
                                                    </Button>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:bg-destructive/10 gap-1.5 justify-center"
                                                    onClick={() => confirmDeleteBox(item.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                    Delete turf
                                                </Button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Weekly bulk schedule dialog ── */}
            <Dialog open={weeklyBulkOpen} onOpenChange={(v) => !saving && setWeeklyBulkOpen(v)}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Partner weekly schedule</DialogTitle>
                        <DialogDescription>
                            <span className="truncate block" title={partnerName}>{partnerName}</span>
                            <span className="text-xs font-mono opacity-70">turf_id {partnerId}</span>
                        </DialogDescription>
                    </DialogHeader>

                    <p className="text-xs text-muted-foreground leading-relaxed">
                        Uses <span className="font-mono">POST /api/turf-slots/weekly_schedule</span>. One transaction per
                        save. Option A applies the same windows Sun–Sat. Option B only changes days you check (empty slot
                        list clears that weekday).
                    </p>

                    {weeklyBulkMsg && (
                        <div
                            className={`rounded-xl border px-3 py-2 text-sm ${
                                weeklyBulkMsg.type === "ok"
                                    ? "border-green-300 bg-green-50 text-green-700"
                                    : "border-destructive/30 bg-destructive/10 text-destructive"
                            }`}
                        >
                            {weeklyBulkMsg.text}
                        </div>
                    )}

                    <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="weeklyBulkMode"
                                className="accent-primary"
                                checked={weeklyBulkMode === "same"}
                                onChange={() => setWeeklyBulkMode("same")}
                            />
                            <span className="text-sm">Same every day (Sun–Sat)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="weeklyBulkMode"
                                className="accent-primary"
                                checked={weeklyBulkMode === "per_day"}
                                onChange={() => setWeeklyBulkMode("per_day")}
                            />
                            <span className="text-sm">Selected days only</span>
                        </label>
                    </div>

                    {weeklyBulkMode === "same" ? (
                        <div className="space-y-3">
                            <div className="rounded-xl border overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-muted/50 text-xs uppercase text-muted-foreground border-b">
                                            <th className="px-3 py-2 text-left font-medium">Start</th>
                                            <th className="px-3 py-2 text-left font-medium">End</th>
                                            <th className="px-3 py-2 text-left font-medium">Price</th>
                                            <th className="px-3 py-2 text-left font-medium">Active</th>
                                            <th className="w-10" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {weeklyBulkSameRows.map((row) => (
                                            <tr key={row.key} className="border-b last:border-0">
                                                <td className="px-3 py-2">
                                                    <Input
                                                        type="time"
                                                        className="h-8 w-[7.25rem]"
                                                        value={row.start_time}
                                                        onChange={(e) =>
                                                            setWeeklyBulkSameRows((rows) =>
                                                                rows.map((r) =>
                                                                    r.key === row.key
                                                                        ? { ...r, start_time: e.target.value }
                                                                        : r,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        type="time"
                                                        className="h-8 w-[7.25rem]"
                                                        value={row.end_time}
                                                        onChange={(e) =>
                                                            setWeeklyBulkSameRows((rows) =>
                                                                rows.map((r) =>
                                                                    r.key === row.key
                                                                        ? { ...r, end_time: e.target.value }
                                                                        : r,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        type="text"
                                                        inputMode="decimal"
                                                        className="h-8 w-24"
                                                        placeholder="0"
                                                        value={row.price}
                                                        onChange={(e) =>
                                                            setWeeklyBulkSameRows((rows) =>
                                                                rows.map((r) =>
                                                                    r.key === row.key
                                                                        ? { ...r, price: e.target.value }
                                                                        : r,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Switch
                                                        checked={row.is_active}
                                                        onCheckedChange={(checked) =>
                                                            setWeeklyBulkSameRows((rows) =>
                                                                rows.map((r) =>
                                                                    r.key === row.key ? { ...r, is_active: checked } : r,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 text-destructive"
                                                        title="Remove"
                                                        onClick={() =>
                                                            setWeeklyBulkSameRows((rows) =>
                                                                rows.filter((r) => r.key !== row.key),
                                                            )
                                                        }
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setWeeklyBulkSameRows((rows) => [...rows, newWeeklyBulkRow()])
                                }
                            >
                                Add time window
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {WEEKDAYS_FOR_BULK.map((wd, di) => (
                                <div
                                    key={wd.v}
                                    className="rounded-xl border bg-card p-3 space-y-2"
                                >
                                    <label className="flex items-center gap-3 cursor-pointer py-0">
                                        <input
                                            type="checkbox"
                                            className="accent-primary h-4 w-4"
                                            checked={weeklyBulkPerDay[di].include}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setWeeklyBulkPerDay((prev) => {
                                                    const next = [...prev];
                                                    let rows = next[di].rows;
                                                    if (checked && rows.length === 0) rows = [newWeeklyBulkRow()];
                                                    next[di] = { include: checked, rows };
                                                    return next;
                                                });
                                            }}
                                        />
                                        <span className="text-sm font-medium">
                                            Update {wd.label}{" "}
                                            <span className="text-xs font-normal text-muted-foreground">
                                                (day {wd.v})
                                            </span>
                                        </span>
                                    </label>
                                    {weeklyBulkPerDay[di].include && (
                                        <>
                                            <div className="rounded-lg border overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="bg-muted/40 text-[0.65rem] uppercase text-muted-foreground border-b">
                                                            <th className="px-2 py-1.5 text-left font-medium">Start</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">End</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">Price</th>
                                                            <th className="px-2 py-1.5 text-left font-medium">On</th>
                                                            <th className="w-10" />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {weeklyBulkPerDay[di].rows.map((row) => (
                                                            <tr key={row.key} className="border-b last:border-0">
                                                                <td className="px-2 py-1.5">
                                                                    <Input
                                                                        type="time"
                                                                        className="h-7 w-[6.75rem] text-xs"
                                                                        value={row.start_time}
                                                                        onChange={(e) =>
                                                                            setWeeklyBulkPerDay((prev) => {
                                                                                const n = [...prev];
                                                                                n[di] = {
                                                                                    ...n[di],
                                                                                    rows: n[di].rows.map((r) =>
                                                                                        r.key === row.key
                                                                                            ? { ...r, start_time: e.target.value }
                                                                                            : r,
                                                                                    ),
                                                                                };
                                                                                return n;
                                                                            })
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="px-2 py-1.5">
                                                                    <Input
                                                                        type="time"
                                                                        className="h-7 w-[6.75rem] text-xs"
                                                                        value={row.end_time}
                                                                        onChange={(e) =>
                                                                            setWeeklyBulkPerDay((prev) => {
                                                                                const n = [...prev];
                                                                                n[di] = {
                                                                                    ...n[di],
                                                                                    rows: n[di].rows.map((r) =>
                                                                                        r.key === row.key
                                                                                            ? { ...r, end_time: e.target.value }
                                                                                            : r,
                                                                                    ),
                                                                                };
                                                                                return n;
                                                                            })
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="px-2 py-1.5">
                                                                    <Input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        className="h-7 w-20 text-xs"
                                                                        value={row.price}
                                                                        onChange={(e) =>
                                                                            setWeeklyBulkPerDay((prev) => {
                                                                                const n = [...prev];
                                                                                n[di] = {
                                                                                    ...n[di],
                                                                                    rows: n[di].rows.map((r) =>
                                                                                        r.key === row.key
                                                                                            ? { ...r, price: e.target.value }
                                                                                            : r,
                                                                                    ),
                                                                                };
                                                                                return n;
                                                                            })
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="px-2 py-1.5">
                                                                    <Switch
                                                                        checked={row.is_active}
                                                                        onCheckedChange={(checked) =>
                                                                            setWeeklyBulkPerDay((prev) => {
                                                                                const n = [...prev];
                                                                                n[di] = {
                                                                                    ...n[di],
                                                                                    rows: n[di].rows.map((r) =>
                                                                                        r.key === row.key
                                                                                            ? { ...r, is_active: checked }
                                                                                            : r,
                                                                                    ),
                                                                                };
                                                                                return n;
                                                                            })
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="px-2 py-1.5">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-6 w-6 text-destructive"
                                                                        onClick={() =>
                                                                            setWeeklyBulkPerDay((prev) => {
                                                                                const n = [...prev];
                                                                                n[di] = {
                                                                                    ...n[di],
                                                                                    rows: n[di].rows.filter(
                                                                                        (r) => r.key !== row.key,
                                                                                    ),
                                                                                };
                                                                                return n;
                                                                            })
                                                                        }
                                                                    >
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="text-xs h-7"
                                                onClick={() =>
                                                    setWeeklyBulkPerDay((prev) => {
                                                        const n = [...prev];
                                                        n[di] = {
                                                            ...n[di],
                                                            rows: [...n[di].rows, newWeeklyBulkRow()],
                                                        };
                                                        return n;
                                                    })
                                                }
                                            >
                                                Add row
                                            </Button>
                                            <p className="text-[11px] text-muted-foreground">
                                                Leave the table empty and save to clear saved slots for this weekday only.
                                            </p>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <DialogFooter className="flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            disabled={saving}
                            onClick={() => setWeeklyBulkOpen(false)}
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            disabled={saving}
                            onClick={() => submitWeeklyBulk()}
                        >
                            {saving ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                                "Save weekly schedule"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Add / Edit box dialog ── */}
            <Dialog open={boxModalOpen} onOpenChange={setBoxModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{boxMode === "add" ? "Add turf" : "Edit turf"}</DialogTitle>
                        <DialogDescription>
                            {boxMode === "add" ? "Create a new bookable turf for this partner." : "Update the turf details."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitBox} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Name</Label>
                            <Input
                                value={boxForm.name}
                                onChange={(e) => setBoxForm({ ...boxForm, name: e.target.value })}
                            />
                            {boxErrors.name && <p className="text-destructive text-xs">{boxErrors.name}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Type</Label>
                            <select
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                value={boxForm.type}
                                onChange={(e) => setBoxForm({ ...boxForm, type: e.target.value })}
                            >
                                <option value="">Select type</option>
                                <option value="Cricket">Cricket</option>
                                <option value="Basketball">Basketball</option>
                                <option value="Wollyball">Wollyball</option>
                                <option value="Football">Football</option>
                                <option value="Other">Other</option>
                            </select>
                            {boxErrors.type && <p className="text-destructive text-xs">{boxErrors.type}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                id="box-active"
                                checked={boxForm.is_active}
                                onCheckedChange={(checked) => setBoxForm({ ...boxForm, is_active: checked })}
                            />
                            <Label htmlFor="box-active" className="cursor-pointer">Active</Label>
                        </div>
                        <DialogFooter className="flex-wrap gap-2">
                            <Button type="button" variant="ghost" onClick={() => setBoxModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                ) : boxMode === "add" ? (
                                    "Create"
                                ) : (
                                    "Save"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ── View slots dialog ── */}
            <Dialog open={viewSlotsOpen} onOpenChange={setViewSlotsOpen}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0 gap-0">
                    <DialogHeader className="shrink-0 px-5 sm:px-6 pt-5 pb-4 border-b">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground pr-10">
                            This turf · by date
                        </p>
                        <DialogTitle className="pr-10">{slotBoxName}</DialogTitle>
                        <DialogDescription>
                            {selectedDateLabel}
                            <span className="text-muted-foreground/70"> · all slots for this day on this turf only</span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="shrink-0 px-5 sm:px-6 py-4 border-b bg-muted/10">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:justify-between">
                            <div className="space-y-1 w-full sm:w-auto sm:min-w-[11rem]">
                                <Label className="text-xs font-medium text-muted-foreground">Pick a date</Label>
                                <Input
                                    type="date"
                                    className="h-8 w-full"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </div>
                            <Button
                                size="sm"
                                className="gap-2 w-full sm:w-auto"
                                onClick={() => {
                                    const box = boxes.find((b: any) => b.id === slotBoxId);
                                    if (box) openSlotModal(box, "add");
                                }}
                            >
                                <PlusCircle className="h-4 w-4" />
                                Add dated slot
                            </Button>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-green-500" />
                                Available
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-yellow-500" />
                                Booked
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-red-500" />
                                Unavailable
                            </span>
                            <span className="inline-flex items-center gap-2 text-muted-foreground/60">
                                <Badge variant="secondary" className="text-xs font-normal px-1.5 py-0">From schedule</Badge>
                                Use Edit to save a dated override for this turf &amp; day
                            </span>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 bg-muted/5">
                        {loadingSlotsForDate ? (
                            <div className="flex flex-col items-center justify-center gap-3 py-16">
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                <p className="text-sm text-muted-foreground">Loading slots…</p>
                            </div>
                        ) : sortedDaySlots.length === 0 ? (
                            <div className="rounded-2xl border border-dashed px-6 py-12 text-center max-w-md mx-auto">
                                <p className="text-sm font-medium">Nothing scheduled this day</p>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                    Add a dated slot to set price and availability for a time window on this turf for this day.
                                </p>
                                <Button
                                    size="sm"
                                    className="mt-5 gap-2"
                                    onClick={() => {
                                        const box = boxes.find((b: any) => b.id === slotBoxId);
                                        if (box) openSlotModal(box, "add");
                                    }}
                                >
                                    <PlusCircle className="h-4 w-4" />
                                    Add slot
                                </Button>
                            </div>
                        ) : (
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {sortedDaySlots.map((slot: any, idx: number) => {
                                    const isResolved =
                                        slot.id == null || slot.resolved_from === "turf_availability";
                                    const isBooked =
                                        !slot.is_available &&
                                        (slot.not_available_reason === 0 || slot.not_available_reason === "0");
                                    const isUnavailable = !slot.is_available && !isBooked;

                                    let statusLabel = "Available";
                                    let statusVariant: "default" | "secondary" | "destructive" | "outline" = "default";
                                    let cardClass = "bg-green-50 border-green-200";
                                    if (isBooked) {
                                        statusLabel = "Booked";
                                        statusVariant = "secondary";
                                        cardClass = "bg-yellow-50 border-yellow-200";
                                    } else if (isUnavailable) {
                                        statusLabel = "Unavailable";
                                        statusVariant = "destructive";
                                        cardClass = "bg-red-50 border-red-200";
                                    }

                                    const timeFrom = String(slot.from_time ?? "").slice(0, 5);
                                    const timeTo = String(slot.to_time ?? "").slice(0, 5);

                                    return (
                                        <li
                                            key={slot.id != null ? slot.id : `r-${idx}-${timeFrom}-${timeTo}`}
                                            className={`rounded-2xl border p-4 shadow-sm ${cardClass}`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 space-y-2">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <Badge variant={statusVariant} className="text-xs">
                                                            {statusLabel}
                                                        </Badge>
                                                        {isResolved && (
                                                            <Badge variant="secondary" className="text-xs font-normal">
                                                                From schedule
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-lg font-semibold tabular-nums tracking-tight">
                                                        {timeFrom}
                                                        <span className="text-muted-foreground font-normal mx-1">–</span>
                                                        {timeTo}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        <span className="font-medium text-foreground">₹{slot.price}</span>
                                                        <span className="text-muted-foreground"> · per slot</span>
                                                    </p>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-1 shrink-0">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 gap-1 text-xs"
                                                        onClick={() => {
                                                            const box = boxes.find((b: any) => b.id === slotBoxId);
                                                            if (!box) return;
                                                            if (isResolved) openSlotModal(box, "add", slot);
                                                            else openSlotModal(box, "update", slot);
                                                        }}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                        {isResolved ? "Override" : "Edit"}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 gap-1 text-xs text-destructive hover:bg-destructive/10"
                                                        onClick={() => {
                                                            if (isResolved) openScheduleRowDeleteExplain(slot);
                                                            else confirmDeleteSlot(slot);
                                                        }}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Schedule row explain dialog (no delete for schedule rows) ── */}
            <Dialog open={scheduleRowExplainOpen} onOpenChange={setScheduleRowExplainOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>No row to delete</DialogTitle>
                        <DialogDescription>
                            This time window is coming from the recurring schedule, not a dated row.
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        This time window is coming from the recurring schedule (or platform defaults), not from a dated
                        row stored only for this turf and day—so there is nothing to remove with Delete yet.
                    </p>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                        To set how this window behaves on{" "}
                        <strong className="font-medium text-foreground">{selectedDateLabel}</strong> for{" "}
                        <strong className="font-medium text-foreground">{slotBoxName}</strong> only, use{" "}
                        <strong className="font-medium text-foreground">Override this window</strong> and save a
                        dated slot for this turf.
                    </p>
                    <DialogFooter className="flex-wrap gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                                setScheduleRowExplainOpen(false);
                                setScheduleRowForExplain(null);
                            }}
                        >
                            Close
                        </Button>
                        <Button type="button" onClick={startOverrideFromScheduleRow}>
                            Override this window
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Add / Edit slot dialog ── */}
            <Dialog open={slotModalOpen} onOpenChange={(v) => { if (!v) closeSlotModal(); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {slotOverrideFromSchedule
                                ? "Override for this day"
                                : slotMode === "add"
                                  ? "Add dated slot"
                                  : "Edit dated slot"}
                        </DialogTitle>
                        <DialogDescription>
                            {slotOverrideFromSchedule
                                ? `Saves a dated slot for ${slotBoxName} on the date you pick—only this turf, only that calendar day.`
                                : slotMode === "add"
                                  ? "Create a new slot for a specific date and time."
                                  : "Update this slot's details."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitSlot} className="space-y-3">
                        <div className="space-y-1.5">
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={slotForm.date}
                                onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })}
                            />
                            {slotErrors.date && <p className="text-destructive text-xs">{slotErrors.date}</p>}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>From</Label>
                                <Input
                                    type="time"
                                    value={slotForm.from_time}
                                    onChange={(e) => setSlotForm({ ...slotForm, from_time: e.target.value })}
                                />
                                {slotErrors.from_time && <p className="text-destructive text-xs">{slotErrors.from_time}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <Label>To</Label>
                                <Input
                                    type="time"
                                    value={slotForm.to_time}
                                    onChange={(e) => setSlotForm({ ...slotForm, to_time: e.target.value })}
                                />
                                {slotErrors.to_time && <p className="text-destructive text-xs">{slotErrors.to_time}</p>}
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Price</Label>
                            <Input
                                type="text"
                                value={slotForm.price}
                                onChange={(e) => setSlotForm({ ...slotForm, price: e.target.value })}
                            />
                            {slotErrors.price && <p className="text-destructive text-xs">{slotErrors.price}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                id="slot-available"
                                checked={slotForm.is_available}
                                onCheckedChange={(checked) =>
                                    setSlotForm((f) => ({
                                        ...f,
                                        is_available: checked,
                                        reason: checked ? "" : f.reason,
                                    }))
                                }
                            />
                            <Label htmlFor="slot-available" className="cursor-pointer">Available</Label>
                        </div>
                        {!slotForm.is_available && (
                            <div className="space-y-1.5">
                                <Label>Unavailable reason</Label>
                                <select
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    value={slotForm.reason === "" ? "" : String(slotForm.reason)}
                                    onChange={(e) =>
                                        setSlotForm({
                                            ...slotForm,
                                            reason: e.target.value === "" ? "" : Number(e.target.value),
                                        })
                                    }
                                >
                                    <option value="">Select reason</option>
                                    <option value="0">Already booked</option>
                                    <option value="1">Partner / turf unavailable</option>
                                </select>
                                {slotErrors.reason && <p className="text-destructive text-xs mt-1">{slotErrors.reason}</p>}
                            </div>
                        )}
                        <DialogFooter className="flex-wrap gap-2 pt-2">
                            <Button type="button" variant="ghost" onClick={closeSlotModal}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                ) : slotOverrideFromSchedule ? (
                                    "Save override"
                                ) : slotMode === "add" ? (
                                    "Add slot"
                                ) : (
                                    "Save changes"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ── Delete box confirmation ── */}
            <Dialog open={deleteBoxModal} onOpenChange={setDeleteBoxModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex flex-col items-center gap-2 text-center">
                            <AlertCircle className="h-10 w-10 text-destructive" />
                            Delete this turf?
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="justify-center flex-wrap gap-2">
                        <Button type="button" variant="ghost" onClick={() => setDeleteBoxModal(false)}>
                            Cancel
                        </Button>
                        <Button type="button" variant="destructive" disabled={saving} onClick={runDeleteBox}>
                            {saving ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                                "Delete"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── Delete slot confirmation ── */}
            <Dialog open={deleteSlotModal} onOpenChange={setDeleteSlotModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex flex-col items-center gap-2 text-center">
                            <AlertCircle className="h-10 w-10 text-destructive" />
                            Delete this slot?
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="justify-center flex-wrap gap-2">
                        <Button type="button" variant="ghost" onClick={() => setDeleteSlotModal(false)}>
                            Cancel
                        </Button>
                        <Button type="button" variant="destructive" disabled={saving} onClick={runDeleteSlot}>
                            {saving ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                                "Delete"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
